import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Loader from "../common/Loader";
import Input from "../common/Input";
import {
  getPromoterPlaceholders,
  createPromoterPlaceholder,
  updatePromoterPlaceholder,
  deletePromoterPlaceholder,
} from "../../services/roomService";
import { color } from "../../styles/theme";

/**
 * Greeting Message — ported from mobile's PromoterPlaceholder concept
 * (onGetPromoterPlaceholder / CreatePromoterPlaceholder / PromoterChatScreen,
 * all reached from setting-a-room's "variable910" row). This entity
 * (PromoterPlaceholder) is account-wide — it has no EventId/RoomId field at
 * all — so the greeting set here applies across every @Room this promoter
 * has, same as the watermark and social links settings. Scoped to a single
 * text greeting (Type=1) here; the backend entity also supports Image/Video
 * types but nothing in this panel creates those.
 */
const GreetingMessageModal = ({ onClose, onSaved }) => {
  const { t } = useTranslation(["rooms", "common"]);
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    getPromoterPlaceholders()
      .then((res) => {
        const list = res.data?.Result || [];
        const first = list[0] || null;
        setExisting(first);
        setTitle(first?.Title || "");
        setContent(first?.Content || "");
      })
      .catch(() => toast.error(t("roomSettings.greetingModal.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  const handleSave = async () => {
    if (!content.trim()) {
      toast.warning(t("roomSettings.greetingModal.contentRequired"));
      return;
    }
    setSaving(true);
    try {
      if (existing?.Id) {
        await updatePromoterPlaceholder(existing.Id, { title: title.trim(), content: content.trim() });
      } else {
        await createPromoterPlaceholder({ title: title.trim(), content: content.trim() });
      }
      toast.success(t("roomSettings.greetingModal.saveSuccess"));
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("roomSettings.greetingModal.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existing?.Id) return;
    setDeleting(true);
    try {
      await deletePromoterPlaceholder(existing.Id);
      toast.success(t("roomSettings.greetingModal.deleteSuccess"));
      setExisting(null);
      setTitle("");
      setContent("");
    } catch (err) {
      toast.error(err.response?.data?.Message || t("roomSettings.greetingModal.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  const busy = saving || deleting;

  return (
    <Modal
      title={t("roomSettings.greetingModal.title")}
      subtitle={t("roomSettings.greetingModal.subtitle")}
      onClose={onClose}
      busy={busy}
      maxWidth={460}
      footer={
        <>
          {existing?.Id && (
            <Button variant="danger" icon={Trash2} onClick={handleDelete} disabled={busy} style={{ marginRight: "auto" }}>
              {deleting ? t("roomSettings.greetingModal.deleting") : t("common:actions.delete")}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={busy}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleSave} disabled={busy}>{saving ? t("roomSettings.greetingModal.saving") : t("common:actions.save")}</Button>
        </>
      }
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          <Input
            label={t("roomSettings.greetingModal.titleLabel")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("roomSettings.greetingModal.titlePlaceholder")}
            style={{ marginBottom: 14 }}
          />
          <label htmlFor="greeting-message-textarea" style={s.label}>{t("roomSettings.greetingModal.contentLabel")}</label>
          <textarea
            id="greeting-message-textarea"
            className="input"
            style={s.textarea}
            rows={5}
            maxLength={500}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t("roomSettings.greetingModal.contentPlaceholder")}
          />
          <div style={s.charCount}>{content.length}/500</div>
        </>
      )}
    </Modal>
  );
};

const s = {
  label: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  textarea: { width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" },
  charCount: { textAlign: "right", fontSize: 11, color: color.textMuted, marginTop: 4 },
};

export default GreetingMessageModal;
